<template>
  <div class="duration-config">
    <div class="duration-input-wrapper">
      <label v-if="label">{{ label }}</label>
      <div class="input-with-icon">
        <input
          v-model="displayValue"
          type="text"
          :placeholder="placeholder"
          :disabled="disabled"
          @input="parseDisplayValue"
          @blur="validateDuration"
        />
        <button v-if="!disabled" type="button" class="icon-button" title="Open Duration Builder" @click="showModal = true">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 3.5V8L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <circle cx="8" cy="8" r="5.5" stroke="currentColor" stroke-width="1.5" />
          </svg>
        </button>
      </div>
      <div v-if="durationError" class="error">{{ durationError }}</div>
      <div v-if="!durationError && parsedMilliseconds !== null" class="helper-text">
        {{ formatReadable() }}
      </div>
    </div>

    <p-modal :visible="showModal">
      <template #title>Duration Configuration</template>

      <div class="modal-content">
        <div class="quick-select">
          <label>Quick Select</label>
          <div class="quick-select-buttons">
            <button
              v-for="preset in presets"
              :key="preset.value"
              type="button"
              class="preset-button"
              :class="{ active: parsedMilliseconds === preset.value }"
              @click="selectPreset(preset)"
            >
              {{ preset.label }}
            </button>
          </div>
        </div>

        <div class="duration-builder">
          <div class="duration-field">
            <label>Days</label>
            <input v-model.number="durationParts.days" type="number" min="0" placeholder="0" @input="buildDuration" />
          </div>

          <div class="duration-field">
            <label>Hours</label>
            <input v-model.number="durationParts.hours" type="number" min="0" max="23" placeholder="0" @input="buildDuration" />
          </div>

          <div class="duration-field">
            <label>Minutes</label>
            <input v-model.number="durationParts.minutes" type="number" min="0" max="59" placeholder="0" @input="buildDuration" />
          </div>

          <div class="duration-field">
            <label>Seconds</label>
            <input v-model.number="durationParts.seconds" type="number" min="0" max="59" placeholder="0" @input="buildDuration" />
          </div>
        </div>

        <div class="duration-preview">
          <label>Duration:</label>
          <code>{{ displayValue || 'Not set' }}</code>
        </div>

        <div class="duration-preview">
          <label>Total time:</label>
          <p>{{ formatReadable() || 'No delay' }}</p>
        </div>

        <div class="duration-preview">
          <label>Milliseconds:</label>
          <code>{{ parsedMilliseconds || 0 }}</code>
        </div>
      </div>

      <template #footer>
        <p-button type="button" @click="clear">Clear</p-button>
        <p-button type="button" @click="showModal = false">Cancel</p-button>
        <p-button type="button" color="primary" @click="applyAndClose">Apply</p-button>
      </template>
    </p-modal>
  </div>
</template>

<script>
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';

export default {
  components: {
    'p-button': Button,
    'p-modal': Modal
  },
  props: {
    value: {
      type: [String, Number, null],
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'e.g., 5m, 2h, 1d 2h 30m'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      displayValue: '',
      parsedMilliseconds: null,
      durationParts: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      },
      durationError: '',
      showModal: false,
      presets: [
        { label: 'No delay', value: null, display: '' },
        { label: '30 seconds', value: 30000, display: '30s' },
        { label: '1 minute', value: 60000, display: '1m' },
        { label: '5 minutes', value: 300000, display: '5m' },
        { label: '10 minutes', value: 600000, display: '10m' },
        { label: '15 minutes', value: 900000, display: '15m' },
        { label: '30 minutes', value: 1800000, display: '30m' },
        { label: '1 hour', value: 3600000, display: '1h' },
        { label: '2 hours', value: 7200000, display: '2h' },
        { label: '4 hours', value: 14400000, display: '4h' },
        { label: '8 hours', value: 28800000, display: '8h' },
        { label: '12 hours', value: 43200000, display: '12h' },
        { label: '1 day', value: 86400000, display: '1d' },
        { label: '2 days', value: 172800000, display: '2d' },
        { label: '1 week', value: 604800000, display: '7d' }
      ]
    };
  },
  watch: {
    value: {
      handler(newVal) {
        this.initializeFromValue(newVal);
      },
      immediate: true
    }
  },
  methods: {
    initializeFromValue(value) {
      if (value === null || value === undefined || value === '') {
        this.displayValue = '';
        this.parsedMilliseconds = null;
        this.resetParts();
        return;
      }

      const ms = parseInt(value);
      if (!isNaN(ms)) {
        this.parsedMilliseconds = ms;
        this.displayValue = this.millisecondsToDisplay(ms);
        this.millisecondsToPartts(ms);
      }
    },
    parseDisplayValue() {
      const value = this.displayValue.trim();

      if (!value) {
        this.parsedMilliseconds = null;
        this.updateValue(null);
        this.resetParts();
        this.durationError = '';
        return;
      }

      // Check if the value contains time units
      const hasTimeUnits = /[dhms]/i.test(value);

      if (!hasTimeUnits) {
        // Just numbers - don't process yet, just show hint
        if (/^\d+$/.test(value)) {
          this.durationError = 'Add time unit: d (days), h (hours), m (minutes), s (seconds)';
        } else {
          this.durationError = 'Invalid format. Use: 1d 2h 30m 15s';
        }
        return; // Don't update anything - this is the key!
      }

      // Clear error since we have time units
      this.durationError = '';

      // Parse formats like "1d 2h 30m 15s" or "2h30m" or "90m"
      const regex = /(\d+)\s*([dhms])/gi;
      let totalMs = 0;
      let match;

      while ((match = regex.exec(value)) !== null) {
        const num = parseInt(match[1]);
        const unit = match[2].toLowerCase();

        switch (unit) {
          case 'd':
            totalMs += num * 24 * 60 * 60 * 1000;
            break;
          case 'h':
            totalMs += num * 60 * 60 * 1000;
            break;
          case 'm':
            totalMs += num * 60 * 1000;
            break;
          case 's':
            totalMs += num * 1000;
            break;
        }
      }

      // Update values only when we have time units
      this.parsedMilliseconds = totalMs;
      this.millisecondsToPartts(totalMs);
      this.updateValue(totalMs);
    },
    millisecondsToDisplay(ms) {
      if (!ms || ms === 0) return '';

      const days = Math.floor(ms / (24 * 60 * 60 * 1000));
      const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
      const seconds = Math.floor((ms % (60 * 1000)) / 1000);

      const parts = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      if (seconds > 0) parts.push(`${seconds}s`);

      return parts.join(' ');
    },
    millisecondsToPartts(ms) {
      if (!ms || ms === 0) {
        this.resetParts();
        return;
      }

      this.durationParts.days = Math.floor(ms / (24 * 60 * 60 * 1000));
      this.durationParts.hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      this.durationParts.minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
      this.durationParts.seconds = Math.floor((ms % (60 * 1000)) / 1000);
    },
    buildDuration() {
      const ms =
        (this.durationParts.days || 0) * 24 * 60 * 60 * 1000 +
        (this.durationParts.hours || 0) * 60 * 60 * 1000 +
        (this.durationParts.minutes || 0) * 60 * 1000 +
        (this.durationParts.seconds || 0) * 1000;

      this.parsedMilliseconds = ms;
      this.displayValue = this.millisecondsToDisplay(ms);
      this.updateValue(ms);
    },
    formatReadable() {
      if (!this.parsedMilliseconds || this.parsedMilliseconds === 0) {
        return 'No delay';
      }

      const days = Math.floor(this.parsedMilliseconds / (24 * 60 * 60 * 1000));
      const hours = Math.floor((this.parsedMilliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      const minutes = Math.floor((this.parsedMilliseconds % (60 * 60 * 1000)) / (60 * 1000));
      const seconds = Math.floor((this.parsedMilliseconds % (60 * 1000)) / 1000);

      const parts = [];
      if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
      if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
      if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
      if (seconds > 0) parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);

      return parts.join(', ') || 'No delay';
    },
    validateDuration() {
      const value = this.displayValue.trim();

      if (!value) {
        this.durationError = '';
        return;
      }

      // Check if it's a valid format
      const hasValidUnits = /(\d+)\s*([dhms])/gi.test(value);
      const isPlainNumber = /^\d+$/.test(value);

      if (!hasValidUnits && !isPlainNumber) {
        this.durationError = 'Invalid format. Use: 1d 2h 30m 15s';
      } else {
        this.durationError = '';

        // Format plain numbers on blur
        if (isPlainNumber) {
          const ms = parseInt(value);
          if (!isNaN(ms) && ms >= 0) {
            const formatted = this.millisecondsToDisplay(ms);
            if (formatted) {
              this.displayValue = formatted;
            }
          }
        }
      }
    },
    selectPreset(preset) {
      this.parsedMilliseconds = preset.value;
      this.displayValue = preset.display;
      if (preset.value) {
        this.millisecondsToPartts(preset.value);
      } else {
        this.resetParts();
      }
      this.updateValue(preset.value);
    },
    clear() {
      this.displayValue = '';
      this.parsedMilliseconds = null;
      this.resetParts();
      this.updateValue(null);
    },
    resetParts() {
      this.durationParts = {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      };
    },
    updateValue(ms) {
      const value = ms === null || ms === 0 ? null : String(ms);
      this.$emit('input', value);
      this.$emit('change', value);
    },
    applyAndClose() {
      if (!this.durationError) {
        this.showModal = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.duration-config {
  .duration-input-wrapper {
    display: grid;
    grid-template-rows: max-content max-content max-content;
    grid-gap: 5px;

    label {
      font-weight: 500;
      font-size: 0.75rem;
      letter-spacing: 0.025em;
    }

    .input-with-icon {
      display: flex;
      align-items: center;
      position: relative;

      input {
        flex: 1;
        padding: 8px 40px 8px 12px;
        font-size: 0.85rem;
        font-weight: 400;
        font-family: inherit;
        border-radius: 2px;
        border: 1px solid var(--theme-on-background-dark);
        color: var(--theme-on-background);
        background: var(--theme-background);
        outline: none;
        transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

        &:not([disabled]) {
          &:hover {
            border-color: var(--theme-on-background);
            color: var(--theme-on-background);
          }

          &:focus,
          &:active {
            border-color: var(--theme-on-background);
            color: var(--theme-on-background);
            outline: none;
          }
        }

        &[disabled] {
          opacity: 0.5;
        }
      }

      .icon-button {
        position: absolute;
        right: 4px;
        padding: 6px;
        background: transparent;
        border: none;
        cursor: pointer;
        color: var(--theme-on-background-dark);
        transition: color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
        border-radius: 2px;

        &:hover {
          color: var(--theme-on-background);
          background: var(--theme-on-background-dark);
          background: rgba(0, 0, 0, 0.04);
        }

        &:focus {
          outline: none;
          background: rgba(0, 0, 0, 0.08);
        }

        svg {
          display: block;
        }
      }
    }

    .error {
      color: var(--theme-error);
      font-size: 0.75rem;
      margin-top: 0.25rem;
    }

    .helper-text {
      color: var(--theme-on-surface-accent);
      font-size: 0.75rem;
      margin-top: 0.25rem;
    }
  }
}

.modal-content {
  padding: 1.5rem 2rem;

  .quick-select {
    margin-bottom: 1.5rem;

    label {
      font-weight: 700;
      font-size: 0.8rem;
      color: var(--theme-on-surface-accent);
      margin-bottom: 0.5rem;
      display: block;
    }

    .quick-select-buttons {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
      grid-gap: 0.5rem;

      .preset-button {
        padding: 6px 12px;
        font-size: 0.75rem;
        font-weight: 400;
        font-family: inherit;
        border-radius: 2px;
        border: 1px solid var(--theme-on-background-dark);
        color: var(--theme-on-background);
        background: var(--theme-background);
        cursor: pointer;
        transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
        text-align: center;

        &:hover {
          border-color: var(--theme-on-background);
          background: var(--theme-surface);
        }

        &.active {
          border-color: var(--theme-primary);
          background: var(--theme-primary);
          color: var(--theme-on-primary);
        }
      }
    }
  }

  .duration-builder {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    grid-gap: 1rem;
    margin-bottom: 1.5rem;

    .duration-field {
      display: grid;
      grid-template-rows: max-content max-content;
      grid-gap: 5px;

      label {
        font-weight: 500;
        font-size: 0.75rem;
        letter-spacing: 0.025em;
      }

      input {
        padding: 8px 12px;
        font-size: 0.85rem;
        font-weight: 400;
        font-family: inherit;
        border-radius: 2px;
        border: 1px solid var(--theme-on-background-dark);
        color: var(--theme-on-background);
        background: var(--theme-background);
        outline: none;
        transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

        &:hover {
          border-color: var(--theme-on-background);
          color: var(--theme-on-background);
        }

        &:focus,
        &:active {
          border-color: var(--theme-on-background);
          color: var(--theme-on-background);
          outline: none;
        }

        // Hide number input arrows
        &::-webkit-inner-spin-button,
        &::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        -moz-appearance: textfield;
      }
    }
  }

  .duration-preview {
    padding: 0.75rem;
    border-radius: 4px;
    background: var(--theme-surface);
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }

    label {
      font-weight: 700;
      font-size: 0.8rem;
      color: var(--theme-on-surface-accent);
    }

    code {
      display: block;
      margin-top: 0.25rem;
      font-family: monospace;
      font-size: 0.9rem;
      color: var(--theme-on-surface);
    }

    p {
      margin: 0.25rem 0 0 0;
      font-size: 0.85rem;
      color: var(--theme-on-surface);
    }
  }
}
</style>
